<!--
  Nuova prenotazione.

  Copre i tre servizi che nascono come booking:
    · Pickup & Delivery   -> type_prenotazione 'pickup' / 'pickup_loaner'
    · Notturno            -> type_prenotazione 'pickup', UNA tratta sola
    · Transfer            -> type_prenotazione 'escort'

  ⚠️ IL SERVIZIO LO DECIDE IL PARTNER, NON QUESTA PAGINA. Alla creazione il
  backend sovrascrive `type_booking` guardando le capability:
  `is_restaurant` -> 'restaurant' (notturno), altrimenti 'booking'. Un partner
  senza `is_restaurant` né `booking_type` viene rifiutato con 403. Per questo
  i servizi qui si accendono in base al partner scelto invece di essere
  sempre tutti disponibili.

  ⚠️ CREARE UN BOOKING NON È UNA SOLA CHIAMATA. Per ogni tratta servono, in
  quest'ordine:
    1. geocodifica degli indirizzi   (POST /planner/open-street-geocode)
       — senza lat/long il planner non calcola niente;
    2. km e imponibile               (POST /pickup/range | /range-restaurant
                                       | /escort/range);
    3. orari liberi                  (POST /planner/slot-availables);
    4. driver + planner              (POST /planner/best-driver) — è QUI che
       nascono i planner, ed è per questo che una prenotazione creata
       altrimenti non è poi assegnabile dalla pagina di dettaglio.
  Solo alla fine si fa POST /booking con le tratte ANNIDATE nel corpo.

  ⚠️ /planner/best-driver NON usa gli status HTTP: quando non trova un driver
  risponde 200 con { error: ... }. Va letto il corpo.
-->
<template>
  <div class="bkn">
    <!-- ---------- intestazione ---------- -->
    <div class="bkn__head">
      <button
        type="button"
        class="bkn__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="bkn__title">
          Nuova prenotazione
        </h1>
        <p class="bkn__sub">
          Pickup &amp; Delivery, Notturno e Transfer. La prenotazione nasce
          da assegnare: driver e jolly si scelgono dal dettaglio.
        </p>
      </div>
    </div>

    <div class="bkn__grid">
      <!-- ================= COLONNA PRINCIPALE ================= -->
      <div class="bkn__col">
        <!-- ---------- 1. partner e servizio ---------- -->
        <section class="bkn__card">
          <header class="bkn__cardHead">
            <span class="bkn__step">1</span>
            <h3>Partner e servizio</h3>
          </header>
          <div class="bkn__cardBody">
            <div class="bkn__row">
              <div class="bkn__field">
                <label>Partner</label>
                <el-select
                  v-model="form.partnerId"
                  placeholder="Scegli il partner"
                  filterable
                  style="width: 100%"
                  @change="onPartnerChange"
                >
                  <el-option
                    v-for="p in partners"
                    :key="p.id"
                    :label="p.company_name"
                    :value="p.id"
                  />
                </el-select>
              </div>
              <div class="bkn__field">
                <label>Sede</label>
                <!-- Le sedi di uno stesso partner si distinguono
                     dall'indirizzo, non dal nome: mostrarlo per intero evita
                     di sceglierne una a caso. -->
                <el-select
                  v-model="form.headquarterId"
                  placeholder="Scegli la sede"
                  :disabled="!form.partnerId"
                  :loading="partnerLoading"
                  style="width: 100%"
                  @change="onHeadquarterChange"
                >
                  <el-option
                    v-for="h in headquarters"
                    :key="h.id"
                    :label="hqLabel(h)"
                    :value="h.id"
                    class="bkn__hqOpt"
                  >
                    <span class="bkn__hqName">{{ h.name || 'Sede' }}</span>
                    <span class="bkn__hqAddr">{{ hqAddress(h) || 'indirizzo non indicato' }}</span>
                  </el-option>
                </el-select>
                <p
                  v-if="form.headquarterId"
                  class="bkn__hint"
                >
                  {{ hqAddress(headquarter) || 'Sede senza indirizzo completo.' }}
                </p>
              </div>
            </div>

            <p
              v-if="partner && !capabilities.length"
              class="bkn__warn"
            >
              Questo partner non è abilitato alle prenotazioni: non ha né
              <code>is_restaurant</code> né <code>booking_type</code>. Il
              server rifiuterebbe la creazione con 403.
            </p>

            <div
              v-if="capabilities.length"
              class="bkn__services"
            >
              <button
                v-for="s in capabilities"
                :key="s.value"
                type="button"
                class="bkn__svc"
                :class="{ 'is-active': form.service === s.value }"
                @click="setService(s.value)"
              >
                <ServiceChip :service="s.chip" />
                <span class="bkn__svcD">{{ s.hint }}</span>
              </button>
            </div>

            <div
              v-if="form.service === 'pickup'"
              class="bkn__opts"
            >
              <el-checkbox v-model="form.loaner">
                Auto di cortesia
              </el-checkbox>
              <!-- `over_twentysix` non si chiede all'operatore: resta nel
                   payload (il backend lo legge) e parte a false. -->
              <el-input
                v-if="form.loaner"
                v-model="form.targa_loaner"
                placeholder="Targa auto di cortesia"
                style="width: 220px"
              />
            </div>

            <!-- MEZZO e PASSEGGERI finiscono su `booking.vehicle_type`
                 e `booking.passengers`, entrambe colonne nullable.
                 ⚠️ Il vincolo "servono tutti e due" vale per le RICHIESTE che
                 arrivano dall'app (booking-request.service.ts rifiuta un
                 transfer senza mezzo o senza passeggeri), NON per la
                 creazione diretta da qui: `POST /booking` non li controlla.
                 Il mezzo resta richiesto perché decide se proporre il
                 noleggio; i passeggeri sono facoltativi. -->
            <div
              v-if="form.service === 'escort'"
              class="bkn__opts"
            >
              <div class="bkn__field bkn__field--veh">
                <label>Mezzo</label>
                <el-select
                  v-model="form.vehicle_type"
                  placeholder="Scegli il mezzo"
                  style="width: 260px"
                >
                  <el-option
                    v-for="v in VEHICLES"
                    :key="v.value"
                    :label="v.label"
                    :value="v.value"
                  />
                </el-select>
              </div>
              <div class="bkn__field bkn__field--veh">
                <label>Passeggeri <span class="bkn__opt">(facoltativo)</span></label>
                <el-input
                  v-model.number="form.passengers"
                  type="number"
                  min="1"
                  placeholder="—"
                  style="width: 140px"
                />
              </div>
              <p
                v-if="seatWarning"
                class="bkn__warn bkn__warn--soft"
              >
                {{ seatWarning }}
              </p>
            </div>

            <!-- NOLEGGIO DA FORNITORE (M9 → COD8).
                 Compare solo per i mezzi che si noleggiano: l'auto del
                 partner e la nostra non hanno un fornitore.
                 È un record SEPARATO (`vehicle_rental`), creato DOPO il
                 booking perché ne ha bisogno dell'id. Registrandolo, il
                 backend riallinea da solo `booking.supplier_cost`: quel campo
                 non si scrive mai a mano. -->
            <div
              v-if="showRental"
              class="bkn__rental"
            >
              <div class="bkn__rentalHead">
                <span class="bkn__rentalTitle">Noleggio del mezzo</span>
                <span class="bkn__spacer" />
                <el-checkbox v-model="form.rental_enabled">
                  Mezzo preso da un fornitore
                </el-checkbox>
              </div>

              <template v-if="form.rental_enabled">
                <div class="bkn__row">
                  <div class="bkn__field">
                    <label>Fornitore</label>
                    <el-select
                      v-model="form.supplierId"
                      placeholder="Scegli il fornitore"
                      filterable
                      style="width: 100%"
                      :loading="suppliersLoading"
                    >
                      <el-option
                        v-for="s in suppliers"
                        :key="s.id"
                        :label="s.company_name"
                        :value="s.id"
                      />
                    </el-select>
                    <p
                      v-if="!suppliersLoading && !suppliers.length"
                      class="bkn__hint bkn__hint--attn"
                    >
                      Nessun fornitore attivo.
                      <button
                        type="button"
                        class="bkn__link"
                        @click="goSuppliers"
                      >
                        Aggiungine uno
                      </button>
                    </p>
                  </div>
                  <div class="bkn__field bkn__field--price">
                    <label>Costo del noleggio</label>
                    <el-input
                      v-model.number="form.rental_cost"
                      type="number"
                      step="0.01"
                      min="0"
                    >
                      <template #append>
                        €
                      </template>
                    </el-input>
                  </div>
                </div>
                <p class="bkn__hint">
                  È quanto paghiamo <b>noi</b> al fornitore, non quanto
                  fatturiamo al cliente. Nasce «da pagare» ed entra nel
                  resoconto COD8; il pagamento si registra da lì, a fattura
                  ricevuta.
                </p>
              </template>
              <p
                v-else-if="rentalUnusual"
                class="bkn__hint"
              >
                Questo mezzo di norma non si noleggia — ma se l'hai preso da un
                fornitore spunta la casella: senza noleggio registrato il costo
                <b>non</b> entra nel COD8.
              </p>
              <p
                v-else
                class="bkn__hint bkn__hint--attn"
              >
                Senza noleggio registrato il costo del mezzo <b>non</b> entra
                nel COD8. Si può aggiungere anche dopo, dal dettaglio della
                prenotazione.
              </p>
            </div>
          </div>
        </section>

        <!-- ---------- 2. cliente ---------- -->
        <section
          class="bkn__card"
          :class="{ 'is-off': !form.service }"
        >
          <header class="bkn__cardHead">
            <span class="bkn__step">2</span>
            <h3>Cliente{{ needsCar ? ' e auto' : '' }}</h3>
          </header>
          <div class="bkn__cardBody">
            <div class="bkn__row">
              <div class="bkn__field">
                <label>Cliente</label>
                <el-select
                  v-model="form.customerId"
                  placeholder="Cerca per cognome…"
                  filterable
                  remote
                  reserve-keyword
                  :remote-method="searchCustomers"
                  :loading="customersLoading"
                  style="width: 100%"
                  @change="onCustomerChange"
                >
                  <el-option
                    v-for="c in customers"
                    :key="c.id"
                    :label="customerLabel(c)"
                    :value="c.id"
                  />
                </el-select>
              </div>
              <div
                v-if="needsCar"
                class="bkn__field"
              >
                <label>Auto del cliente</label>
                <el-select
                  v-model="form.carId"
                  placeholder="Scegli l'auto"
                  :disabled="!form.customerId"
                  :loading="carsLoading"
                  style="width: 100%"
                >
                  <el-option
                    v-for="c in cars"
                    :key="c.id"
                    :label="carLabel(c)"
                    :value="c.id"
                  />
                </el-select>
                <p
                  v-if="form.customerId && !carsLoading && !cars.length"
                  class="bkn__hint bkn__hint--attn"
                >
                  Il cliente non ha auto registrate: aggiungile dalla sua
                  scheda, servono per questo servizio.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- ---------- 3. tratte ---------- -->
        <section
          class="bkn__card"
          :class="{ 'is-off': !form.service }"
        >
          <header class="bkn__cardHead">
            <span class="bkn__step">3</span>
            <h3>Tratte</h3>
            <div class="bkn__spacer" />
            <!-- Il notturno è UNA tratta sola: niente da scegliere. -->
            <el-checkbox-group
              v-if="form.service && form.service !== 'restaurant'"
              v-model="activeLegs"
              size="small"
            >
              <el-checkbox-button label="gone">
                Andata
              </el-checkbox-button>
              <el-checkbox-button label="return">
                Ritorno
              </el-checkbox-button>
            </el-checkbox-group>
          </header>
          <div class="bkn__cardBody">
            <p
              v-if="!form.service"
              class="bkn__hint"
            >
              Scegli prima partner e servizio.
            </p>

            <!-- Il planner cerca i driver per PROVINCIA della sede. Senza,
                 la ricerca si ferma con un errore invece di dare orari. -->
            <p
              v-if="form.headquarterId && !headquarter?.provinceId"
              class="bkn__warn"
            >
              Questa sede non ha una <b>provincia</b> assegnata. Il planner
              cerca i driver per provincia: senza, non tornerà nessun orario. Va
              impostata sulla sede prima di continuare.
            </p>

            <!-- Capacità driver della provincia: gli orari in cui la provincia
                 ha già tutti i driver (o i jolly) impegnati non si vedono e non
                 si salvano. Solo Simba può superarla, e lo sceglie qui. -->
            <div
              v-if="form.service"
              class="bkn__capacity"
            >
              <el-checkbox
                v-model="ignoreCapacity"
                @change="reloadSlots"
              >
                Ignora la capacità driver della provincia
              </el-checkbox>
              <p
                v-if="ignoreCapacity"
                class="bkn__hint bkn__hint--attn"
              >
                Si vedono e si salvano anche gli orari in cui la provincia ha già
                tutti i driver impegnati: la corsa andrà coperta a mano.
              </p>
            </div>

            <article
              v-for="leg in visibleLegs"
              :key="leg.key"
              class="bkn__leg"
            >
              <div class="bkn__legHead">
                <span class="bkn__legTag">{{ leg.title }}</span>
                <div class="bkn__spacer" />
                <span
                  v-if="leg.km"
                  class="bkn__legKm"
                >{{ Number(leg.km).toLocaleString('it-IT', { maximumFractionDigits: 1 }) }} km · fascia {{ Number.isFinite(Number(leg.kmRange)) ? Number(leg.kmRange).toLocaleString('it-IT', { maximumFractionDigits: 1 }) : leg.kmRange }}</span>
              </div>

              <!-- L'altro capo della tratta è la SEDE del partner: sul
                   notturno è la partenza, sul transfer dipende dalla
                   direzione. Quando non lo è, si indica a mano — e da lì
                   partono anche km, prezzo e ricerca degli slot. -->
              <div
                v-if="form.service !== 'pickup'"
                class="bkn__originSwitch"
              >
                <el-checkbox
                  v-model="leg.customOrigin"
                  @change="onOriginModeChange(leg)"
                >
                  {{ form.service === 'escort'
                    ? (leg.key === 'gone'
                      ? 'Arrivo diverso dalla sede'
                      : 'Partenza diversa dalla sede')
                    : 'Partenza da un punto diverso dalla sede' }}
                </el-checkbox>
                <span
                  v-if="!leg.customOrigin"
                  class="bkn__originHint"
                >{{ otherEndHint(leg) }}</span>
              </div>

              <!-- indirizzi -->
              <div class="bkn__points">
                <div
                  v-for="pt in pointsOf(leg)"
                  :key="pt.key"
                  class="bkn__point"
                >
                  <span class="bkn__pointLbl">{{ pt.label }}</span>
                  <div class="bkn__addr">
                    <el-input
                      v-model="pt.model.address"
                      placeholder="Via/piazza"
                      class="bkn__addrStreet"
                      @input="pt.model.fromCustomer = false"
                    />
                    <el-input
                      v-model="pt.model.street_number"
                      placeholder="N°"
                      class="bkn__addrN"
                      @input="pt.model.fromCustomer = false"
                    />
                    <el-input
                      v-model="pt.model.cap"
                      placeholder="CAP"
                      class="bkn__addrCap"
                      @input="pt.model.fromCustomer = false"
                    />
                    <el-input
                      v-model="pt.model.city"
                      placeholder="Città"
                      class="bkn__addrCity"
                      @input="pt.model.fromCustomer = false"
                    />
                    <el-input
                      v-model="pt.model.province"
                      placeholder="PR"
                      class="bkn__addrPr"
                      @input="pt.model.fromCustomer = false"
                    />
                    <el-button
                      :loading="pt.model.geocoding"
                      @click="geocode(leg, pt.model)"
                    >
                      Verifica
                    </el-button>
                  </div>
                  <p
                    v-if="pt.model.label"
                    class="bkn__hint bkn__hint--ok"
                  >
                    ✓ {{ pt.model.label }}
                  </p>
                  <p
                    v-else-if="pt.model.geoError"
                    class="bkn__hint bkn__hint--attn"
                  >
                    {{ pt.model.geoError }}
                  </p>
                </div>
              </div>

              <!-- data e orario -->
              <div class="bkn__row bkn__row--tight">
                <div class="bkn__field">
                  <label>Data</label>
                  <el-date-picker
                    v-model="leg.date"
                    type="date"
                    placeholder="gg/mm/aaaa"
                    format="DD/MM/YYYY"
                    value-format="YYYY-MM-DD"
                    style="width: 100%"
                    :disabled="!legGeocoded(leg)"
                    @change="onLegDate(leg)"
                  />
                </div>
                <div class="bkn__field">
                  <label>Orario</label>
                  <el-select
                    v-model="leg.slot"
                    v-loading="leg.slotsLoading"
                    placeholder="Scegli l'orario"
                    :disabled="!leg.date || leg.slotsLoading"
                    filterable
                    style="width: 100%"
                    @change="onLegSlot(leg)"
                  >
                    <el-option
                      v-for="s in leg.slots"
                      :key="s.value"
                      :label="s.value"
                      :value="s.value"
                      :disabled="s.disabled"
                    />
                  </el-select>
                </div>
              </div>

              <!-- Arrivo stimato: l'orario scelto è la PARTENZA, e senza la
                   durata non si sa quando la corsa finisce. -->
              <div
                v-if="leg.slot && arrivalOf(leg)"
                class="bkn__arrival"
              >
                <span class="bkn__arrivalMain">
                  Parte alle <b>{{ leg.slot }}</b> · arrivo previsto
                  <b>{{ arrivalOf(leg) }}</b>
                </span>
                <span class="bkn__arrivalSub">
                  {{ Math.round(Number(leg.minute)) }} min di tragitto
                </span>
                <span
                  v-if="form.service === 'restaurant'"
                  class="bkn__fascia"
                  :class="{ 'bkn__fascia--night': isNightHour(leg.slot) }"
                >
                  tariffa {{ isNightHour(leg.slot) ? 'serale' : 'diurna' }}
                </span>
              </div>
              <!-- ---------- prezzo ---------- -->
              <!-- Il calcolo è una PROPOSTA: con il motore a colonne un
                   partner senza listino legacy quota 0, e comunque il prezzo
                   commerciale resta una decisione di chi vende. -->
              <div
                v-if="legGeocoded(leg)"
                class="bkn__price"
              >
                <!-- Sul TRANSFER il prezzo si scrive IVA inclusa (è il totale
                     fattura); l'imponibile se ne ricava. Sugli altri servizi
                     resta l'imponibile. -->
                <div
                  v-if="form.service === 'escort'"
                  class="bkn__field bkn__field--price"
                >
                  <label>Totale della tratta (IVA inclusa)</label>
                  <el-input
                    :model-value="lordoInput(leg)"
                    placeholder="0,00"
                    @update:model-value="(v) => setLordo(leg, v)"
                    @blur="normalizeLordo(leg)"
                  >
                    <template #prepend>
                      €
                    </template>
                  </el-input>
                  <span
                    v-if="leg.imponibile !== null && leg.imponibile !== ''"
                    class="bkn__hint"
                  >Imponibile {{ euro(leg.imponibile) }} + IVA 22%</span>
                </div>
                <div
                  v-else
                  class="bkn__field bkn__field--price"
                >
                  <label>Imponibile della tratta</label>
                  <el-input
                    v-model="leg.imponibile"
                    placeholder="0,00"
                    @blur="normalizePrice(leg)"
                  >
                    <template #prepend>
                      €
                    </template>
                  </el-input>
                </div>
                <div class="bkn__priceSide">
                  <!-- Sul notturno il listino dipende dall'ora: dirlo evita
                       di far cercare un prezzo che non può ancora esistere. -->
                  <p
                    v-if="needsSlotForPrice(leg)"
                    class="bkn__hint"
                  >
                    Scegli l'orario: sul notturno il prezzo cambia fra le fasce
                    diurna e serale (dalle 22 alle 6).
                  </p>
                  <p
                    v-else-if="leg.imponibileAuto === null"
                    class="bkn__hint"
                  >
                    Non ancora calcolato.
                  </p>
                  <p
                    v-else-if="priceEdited(leg)"
                    class="bkn__hint bkn__hint--edit"
                  >
                    Modificato a mano — calcolato:
                    {{ form.service === 'escort' ? `${euro(lordoCalcolato(leg))} IVA inclusa` : euro(leg.imponibileAuto) }}
                    <button
                      type="button"
                      class="bkn__link"
                      @click="resetPrice(leg)"
                    >
                      ripristina
                    </button>
                  </p>
                  <!-- Con TARIFF_ENGINE non impostato il server legge le 64
                       colonne legacy e IGNORA le fasce: un partner con le
                       fasce compilate quota comunque 0. Va detto, altrimenti
                       si cerca l'errore nel listino sbagliato. -->
                  <p
                    v-else-if="Number(leg.imponibileAuto) === 0"
                    class="bkn__hint bkn__hint--attn"
                  >
                    Il motore tariffe ha risposto <b>0</b>. O il partner non ha
                    un listino per <b>{{ tariffServiceLabel }}</b>, o il server
                    sta ancora usando il listino a colonne e ignora le fasce
                    (<code>TARIFF_ENGINE=bands</code>). Intanto puoi mettere il
                    prezzo a mano.
                  </p>
                  <p
                    v-else
                    class="bkn__hint bkn__hint--ok"
                  >
                    Calcolato dal listino del partner.
                  </p>
                </div>
              </div>

              <p
                v-if="!legGeocoded(leg)"
                class="bkn__hint"
              >
                Verifica gli indirizzi: senza coordinate il planner non può
                calcolare né gli orari né il prezzo.
              </p>
              <!-- "Nessuno slot" ha cause diverse e vanno distinte, altrimenti
                   si cerca il problema nel posto sbagliato. -->
              <p
                v-else-if="leg.slotsError"
                class="bkn__hint bkn__hint--attn"
              >
                Il calcolo degli orari non è riuscito ({{ leg.slotsError }}):
                non è che non ci sia posto, è che la risposta non è arrivata.
              </p>
              <template v-else-if="leg.date && !leg.slotsLoading && !freeSlots(leg)">
                <p class="bkn__hint bkn__hint--attn">
                  Nessun orario disponibile in questa giornata.
                  <template v-if="!ignoreCapacity">
                    Può dipendere anche dalla capacità driver di
                    <b>{{ provinceName || 'questa provincia' }}</b>: le corse già prese
                    (anche senza driver) la occupano.
                  </template>
                </p>
                <p
                  v-if="needsJolly"
                  class="bkn__hint"
                >
                  Su questo servizio il motore pretende, per la stessa ora,
                  <b>un driver e un jolly</b>: se in
                  <b>{{ provinceName || 'questa provincia' }}</b> nessun driver ha il
                  ruolo <b>Jolly</b> (campo <code>is_jolly</code> nella sua
                  scheda) la giornata risulta piena a qualsiasi ora. Il
                  Transfer invece il jolly non lo richiede: se lì gli orari
                  compaiono, è questo il motivo.
                </p>
              </template>
            </article>
          </div>
        </section>

        <!-- ---------- 4. lavorazioni e pagamento in app ---------- -->
        <!-- ⚠️ "Pagamento in app" è la entità `task`: la spunta `pay` è ciò
             che fa arrivare al cliente la notifica da SALDARE (pay_task_*).
             Senza spunta la lavorazione gli compare soltanto da accettare. -->
        <section
          v-if="form.service"
          class="bkn__card"
        >
          <header class="bkn__cardHead">
            <span class="bkn__step">4</span>
            <h3>Lavorazioni e pagamento in app</h3>
            <div class="bkn__spacer" />
            <el-button
              size="small"
              @click="addTask"
            >
              Aggiungi
            </el-button>
          </header>
          <div class="bkn__cardBody">
            <p
              v-if="!form.tasks.length"
              class="bkn__hint"
            >
              Nessuna voce. Metti qui quello che il cliente deve vedere — e,
              con la spunta, pagare — dall'app.
            </p>
            <div
              v-if="form.tasks.length"
              class="bkn__taskHead"
            >
              <span>Descrizione</span>
              <span class="bkn__taskNum">Importo</span>
              <span>In app</span>
              <span />
            </div>
            <div
              v-for="(t, i) in form.tasks"
              :key="i"
              class="bkn__task"
            >
              <el-input
                v-model="t.description"
                placeholder="Es. tagliando, cambio gomme…"
              />
              <el-input
                v-model="t.cost"
                placeholder="0,00"
                class="bkn__taskCost"
              >
                <template #prepend>
                  €
                </template>
              </el-input>
              <!-- `pay` è una RICHIESTA di pagamento, non un pagamento
                   avvenuto: quello è `accepted`, che il cliente valorizza
                   saldando dall'app. -->
              <el-checkbox
                v-model="t.pay"
                class="bkn__taskPay"
              >
                Da pagare
              </el-checkbox>
              <el-button
                text
                type="danger"
                class="bkn__taskDel"
                @click="form.tasks.splice(i, 1)"
              >
                Togli
              </el-button>
            </div>
            <el-button
              v-if="!form.tasks.length && payableDefault.amount > 0"
              size="small"
              text
              @click="addTripTask"
            >
              ＋ Chiedi al cliente {{ euro(payableDefault.amount) }} in app
            </el-button>
            <p
              v-if="!form.tasks.length && payableDefault.amount > 0"
              class="bkn__hint"
            >
              {{ payableDefault.detail }}
            </p>
          </div>
        </section>

        <!-- ---------- note ---------- -->
        <section
          v-if="form.service"
          class="bkn__card"
        >
          <header class="bkn__cardHead">
            <h3>Note</h3>
          </header>
          <div class="bkn__cardBody">
            <el-input
              v-model="form.note"
              type="textarea"
              :rows="3"
              placeholder="Note interne sulla prenotazione"
            />
          </div>
        </section>
      </div>

      <!-- ================= RIEPILOGO STICKY ================= -->
      <aside class="bkn__aside">
        <section class="bkn__card">
          <header class="bkn__cardHead">
            <h3>Riepilogo</h3>
          </header>
          <div class="bkn__cardBody">
            <div class="bkn__kv">
              <span>Servizio</span>
              <b>{{ serviceLabel }}</b>
            </div>
            <div class="bkn__kv">
              <span>Partner</span>
              <b>{{ partner?.company_name || '—' }}</b>
            </div>
            <div class="bkn__kv">
              <span>Cliente</span>
              <b>{{ selectedCustomerLabel }}</b>
            </div>

            <div
              v-for="leg in visibleLegs"
              :key="leg.key"
              class="bkn__sumLeg"
            >
              <div class="bkn__sumLegTop">
                <span>{{ leg.title }}</span>
                <b>{{ leg.date ? `${dLabel(leg.date)} ${leg.slot || ''}` : '—' }}</b>
              </div>
              <div
                v-if="leg.imponibile !== null"
                class="bkn__sumLegRow"
              >
                <span>Imponibile</span>
                <b>{{ euro(leg.imponibile) }}</b>
              </div>
            </div>

            <div class="bkn__tot">
              <div class="bkn__totRow">
                <span>Imponibile</span>
                <b>{{ euro(totals.imponibile) }}</b>
              </div>
              <div class="bkn__totRow bkn__totRow--big">
                <span>Totale fattura</span>
                <b>{{ euro(totals.totFattura) }}</b>
              </div>
            </div>
          </div>
        </section>

        <section class="bkn__card">
          <header class="bkn__cardHead">
            <h3>Prima di creare</h3>
          </header>
          <div class="bkn__cardBody">
            <div
              v-for="c in checklist"
              :key="c.label"
              class="bkn__check"
              :class="{ 'is-ok': c.ok }"
            >
              <span class="bkn__checkIco">{{ c.ok ? '✓' : '!' }}</span>
              <span>
                {{ c.label }}
                <!-- Quando manca qualcosa si dice COSA: «Tratte con
                     indirizzo, data e orario» da solo non fa capire che il
                     ritorno è senza orario. -->
                <span
                  v-for="d in (c.ok ? [] : c.details || [])"
                  :key="d"
                  class="bkn__checkWhy"
                >{{ d }}</span>
              </span>
            </div>
            <el-button
              type="primary"
              class="bkn__submit"
              :disabled="!canSubmit"
              :loading="submitting"
              @click="submit"
            >
              Crea prenotazione
            </el-button>
            <p class="bkn__hint">
              La prenotazione nasce <b>da assegnare</b>: driver e jolly si
              scelgono poi dalla pagina di dettaglio. Il controllo di copertura
              resta — se per un orario non c'è nessuno libero in zona, la
              prenotazione non viene creata.
            </p>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import bookingAPI from '../../services/bookingAPI.js';
import prenotazioneAPI from '../../services/prenotazioneAPI.js';
import { VEHICLES, vehicleOf } from '../../services/vehicleTypes.js';
import supplierAPI from '../../services/supplierAPI.js';
import { toInstant, giornoDiOggi } from '../../services/datetime.js';
import partnerAPI from '../../services/partnerAPI.js';
import customerAPI from '../../services/customerAPI.js';
import carAPI from '../../services/carAPI.js';
import ServiceChip from '../../components/ui/ServiceChip.vue';

// Griglia degli orari. Deve combaciare con quella del backend, che in
// `getTrueDuration` va da 00:00 a MAX_SLOT_MINUTE (23:45) con passo 15':
// il notturno lavora anche di sera.
const ALL_SLOTS = (() => {
  const out = [];
  for (let m = 0; m <= 23 * 60 + 45; m += 15) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return out;
})();

// Il notturno cambia listino alle 22 e alle 6 (pickup.service.ts:
// `hour >= 22 || hour < 6`): serve a dirlo accanto all'orario scelto.
const isNightHour = (hhmm) => {
  if (!hhmm) return false;
  const h = Number(String(hhmm).slice(0, 2));
  return h >= 22 || h < 6;
};

const emptyPoint = () => ({
  address: '', street_number: '', cap: '', city: '', province: '',
  label: '', latitude: 0, longitude: 0, geocoding: false, geoError: '',
});

const emptyLeg = (key, title) => ({
  key,
  title,
  // `point` è SEMPRE il capo del cliente (casa sua). L'altro capo è la SEDE
  // del partner, salvo eccezione: allora vive in `point2`.
  //   pickup   -> andata casa→sede, ritorno sede→casa
  //   transfer -> stessa convenzione, ma l'altro capo si può cambiare
  //   notturno -> si parte dalla sede, salvo partenza diversa
  point: emptyPoint(),
  point2: emptyPoint(),
  customOrigin: false,
  date: '',
  slot: '',
  slots: ALL_SLOTS.map((v) => ({ value: v, disabled: true })),
  slotsLoading: false,
  slotsError: '',
  km: null, kmRange: null, cost: null, minute: null,
  // `imponibile` è il valore EFFETTIVO (modificabile a mano); `imponibileAuto`
  // resta quello proposto dal motore tariffe, per poterlo confrontare e
  // ripristinare.
  imponibile: null, imponibileAuto: null, lordoAuto: null,
  // TRANSFER: il prezzo a mano si scrive IVA inclusa (`lordoTesto`, il testo
  // del campo); l'imponibile se ne ricava (÷ 1,22). null = vale l'imponibile.
  lordoTesto: null,
});

// Tipo richiesto da slot-availables e best-driver (enum BookingType).
const slotType = (service, loaner, direction) => {
  if (service === 'escort') {
    return loaner
      ? (direction === 'gone' ? 'ESCORT_AUTO_GONE' : 'ESCORT_AUTO_RETURN')
      : (direction === 'gone' ? 'ESCORT_GONE' : 'ESCORT_RETURN');
  }
  // il notturno viaggia sulle tratte pickup, sempre come 'return'
  if (service === 'restaurant') return 'PICKUP_RETURN';
  return loaner
    ? (direction === 'gone' ? 'PICKUP_AUTO_GONE' : 'PICKUP_AUTO_RETURN')
    : (direction === 'gone' ? 'PICKUP_GONE' : 'PICKUP_RETURN');
};

// Il planner restituisce i tempi dentro `trips`, distinti da `type`:
// date_c1_p1 = quando il driver è dal cliente, date_p1_c1 = quando parte.
const tripDate = (trips, type) =>
  (trips || []).find((t) => t.type === type)?.dateStart ?? null;

export default {
  name: 'AddBookingPage',
  components: { ServiceChip },

  setup() {
    const router = useRouter();

    const partners = ref([]);
    const partner = ref(null);
    const partnerLoading = ref(false);
    const customers = ref([]);
    const customersLoading = ref(false);
    const cars = ref([]);
    const carsLoading = ref(false);
    const submitting = ref(false);

    const form = reactive({
      partnerId: '',
      headquarterId: '',
      service: '',
      loaner: false,
      over_twentysix: false,
      targa_loaner: '',
      // Transfer: obbligatori, come per le richieste che arrivano dall'app
      vehicle_type: '',
      passengers: null,
      // noleggio: record separato, creato dopo il booking
      rental_enabled: false,
      supplierId: '',
      rental_cost: null,
      customerId: '',
      carId: '',
      note: '',
      tasks: [],
    });

    const legs = reactive({
      gone: emptyLeg('gone', '↗ Andata'),
      return: emptyLeg('return', '↘ Ritorno'),
    });
    const activeLegs = ref(['gone', 'return']);

    const headquarters = computed(() => partner.value?.headquarters || []);
    const headquarter = computed(() =>
      headquarters.value.find((h) => h.id === form.headquarterId) || null);

    // ---- capability -> servizi ammessi ------------------------------------
    // Le stesse regole che il backend applica in applyBookingCapability: qui
    // servono per non far compilare un form che finirebbe in 403.
    const capabilities = computed(() => {
      const p = partner.value;
      if (!p) return [];
      const out = [];
      if (p.is_restaurant) {
        out.push({
          value: 'restaurant', chip: 'restaurant',
          hint: 'Una tratta sola, accompagnamento serale',
        });
      } else if (p.booking_type) {
        out.push({
          value: 'pickup', chip: 'pickup',
          hint: "Ritiro e riconsegna dell'auto",
        });
      }
      // Il Transfer non ha una capability propria: dipende solo da F6, e il
      // backend lo concede anche ai partner notturni — `is_restaurant` sceglie
      // il listino del pickup, non nega il Transfer (vedi EscortService).
      if (p.booking_type) {
        out.push({
          value: 'escort', chip: 'escort',
          hint: 'Accompagnamento del cliente',
        });
      }
      return out;
    });

    const serviceLabel = computed(() => ({
      pickup: form.loaner ? 'Pickup · auto di cortesia' : 'Pickup & Delivery',
      restaurant: 'Notturno',
      escort: 'Transfer',
    }[form.service] || '—'));

    // Il notturno ha una sola tratta, che a database è un 'return'.
    const visibleLegs = computed(() => {
      if (!form.service) return [];
      if (form.service === 'restaurant') return [legs.return];
      return activeLegs.value
        .slice()
        .sort((a) => (a === 'gone' ? -1 : 1))
        .map((k) => legs[k]);
    });

    const needsCar = computed(() => form.service && form.service !== 'escort');

    // Il servizio con cui il motore cerca le fasce NON è quello scelto a UI:
    // l'auto di cortesia e la fascia notturna hanno listini propri
    // (`pickup_loaner`, `restaurant_night`), che la creazione partner non
    // compila. Mostrarne il nome evita di cercare nel listino sbagliato.
    const tariffService = computed(() => {
      if (form.service === 'escort') return form.loaner ? 'escort_loaner' : 'escort';
      if (form.service === 'restaurant') {
        const h = Number((legs.return.slot || '').slice(0, 2));
        return Number.isFinite(h) && (h >= 22 || h < 6) ? 'restaurant_night' : 'restaurant';
      }
      return form.loaner ? 'pickup_loaner' : 'pickup';
    });
    const TARIFF_LABELS = {
      pickup: 'Pickup',
      pickup_loaner: 'Pickup con auto di cortesia',
      escort: 'Transfer',
      escort_loaner: 'Transfer con auto del partner',
      restaurant: 'Notturno',
      restaurant_night: 'Notturno · fascia serale (22–06)',
    };
    const tariffServiceLabel = computed(
      () => TARIFF_LABELS[tariffService.value] || tariffService.value);

    // Il motore esige driver + jolly nella stessa ora per le tratte pickup
    // (isJollyNeeded lato backend); il transfer si accontenta del driver.
    const needsJolly = computed(() => !!form.service && form.service !== 'escort');
    const provinceName = computed(() => headquarter.value?.province_ref?.name || headquarter.value?.province || '');

    // ---- caricamenti ------------------------------------------------------
    const loadPartners = async () => {
      try {
        const r = await partnerAPI.getPartners();
        partners.value = (r.data?.data ?? r.data ?? []);
      } catch (e) {
        console.warn('Elenco partner non disponibile:', e?.message);
      }
    };
    loadPartners();

    const onPartnerChange = async (id) => {
      form.headquarterId = '';
      form.service = '';
      partner.value = null;
      if (!id) return;
      partnerLoading.value = true;
      try {
        const r = await partnerAPI.getPartnerDetail(id);
        partner.value = r.data;
        if (headquarters.value.length === 1) {
          form.headquarterId = headquarters.value[0].id;
          onHeadquarterChange();
        }
        if (capabilities.value.length === 1) setService(capabilities.value[0].value);
      } catch (e) {
        console.error('Partner non caricato:', e);
      } finally {
        partnerLoading.value = false;
      }
    };

    // Cambiando sede cambiano prezzi e slot: quanto calcolato non vale più.
    const onHeadquarterChange = () => {
      Object.values(legs).forEach(resetLegCalc);
    };

    const setService = (value) => {
      if (form.service === value) return;
      form.service = value;
      form.carId = '';
      form.loaner = false;
      activeLegs.value = value === 'restaurant' ? ['return'] : ['gone', 'return'];
      Object.values(legs).forEach((l) => {
        resetLegCalc(l);
        l.point2 = emptyPoint();
        // L'indirizzo del cliente resta: km, prezzo e durata si ricalcolano col
        // listino del servizio nuovo (senza durata la corsa avrebbe partenza = arrivo).
        if (activeLegs.value.includes(l.key) && legGeocoded(l)) calcPrice(l);
      });
      // Il Transfer non chiede l'auto: passando a un servizio che la chiede
      // vanno caricate quelle del cliente già scelto.
      if (form.customerId && needsCar.value) loadCars(form.customerId);
    };

    const searchCustomers = async (q) => {
      if (!q || q.length < 2) return;
      customersLoading.value = true;
      try {
        const r = await customerAPI.getCustomersPaged({ search: q, limit: 20 });
        customers.value = r.rows;
      } catch (e) {
        console.warn('Ricerca clienti fallita:', e?.message);
      } finally {
        customersLoading.value = false;
      }
    };

    // L'indirizzo del cliente è già in anagrafica, coordinate comprese: non
    // ha senso farlo riscrivere. Si compila il capo "casa" di ogni tratta —
    // solo se vuoto, per non cancellare quello che l'operatore ha già messo.
    const fillCustomerAddress = (customer) => {
      if (!customer?.address) {
        // Niente indirizzo in anagrafica: via quello lasciato dal cliente
        // scelto prima, che altrimenti resterebbe sulla prenotazione.
        Object.values(legs).forEach((leg) => {
          if (!leg.point.fromCustomer) return;
          resetLegCalc(leg);
          leg.point = emptyPoint();
        });
        return;
      }
      const filled = {
        address: customer.address || '',
        street_number: String(customer.street_number ?? ''),
        cap: String(customer.cap ?? ''),
        city: customer.city || '',
        province: customer.province || '',
        latitude: Number(customer.latitude) || 0,
        longitude: Number(customer.longitude) || 0,
      };
      Object.values(legs).forEach((leg) => {
        const p = leg.point;
        // Un indirizzo scritto a mano non si tocca; quello compilato qui per un
        // ALTRO cliente sì, altrimenti la prenotazione partirebbe da casa sua.
        if ((p.address || p.city) && !p.fromCustomer) return;
        if (p.fromCustomer) resetLegCalc(leg);
        Object.assign(p, filled, { geoError: '', label: '', fromCustomer: true });
        // Se l'anagrafica ha già le coordinate la tratta è pronta; altrimenti
        // si cerca l'indirizzo una volta sola, qui.
        //
        // ⚠️ In ENTRAMBI i rami va calcolato il prezzo: `calcPrice` vive dentro
        // `geocode`, e senza `leg.minute` la corsa si salverebbe con partenza e
        // arrivo identici.
        if (p.latitude && p.longitude) {
          p.label = `${filled.address} ${filled.street_number}, ${filled.city}`.trim();
          if (legGeocoded(leg)) calcPrice(leg);
        } else {
          geocode(leg, p);
        }
      });
    };

    const onCustomerChange = async (id) => {
      form.carId = '';
      cars.value = [];
      if (!id) return;

      fillCustomerAddress(customers.value.find((c) => c.id === id));

      if (needsCar.value) await loadCars(id);
    };

    const loadCars = async (id) => {
      carsLoading.value = true;
      try {
        const r = await carAPI.getCarsByCustomer(id);
        cars.value = r.data?.data ?? r.data ?? [];
        if (cars.value.length === 1) form.carId = cars.value[0].id;
      } catch (e) {
        console.warn('Auto del cliente non caricate:', e?.message);
      } finally {
        carsLoading.value = false;
      }
    };

    // ---- punti, slot, prezzo ----------------------------------------------
    const pointsOf = (leg) => {
      // Transfer: la convenzione è quella del pickup — andata dalla casa del
      // cliente alla sede, ritorno all'incontrario. L'altro capo si scrive a
      // mano solo se `customOrigin` dice che non è la sede.
      if (form.service === 'escort') {
        const home = {
          key: 'a',
          label: leg.key === 'gone' ? 'Partenza · indirizzo del cliente' : 'Arrivo · indirizzo del cliente',
          model: leg.point,
        };
        if (!leg.customOrigin) return [home];
        const other = {
          key: 'b',
          label: leg.key === 'gone' ? 'Arrivo' : 'Partenza',
          model: leg.point2,
        };
        return leg.key === 'gone' ? [home, other] : [other, home];
      }
      // Notturno con partenza diversa dalla sede: il punto scelto vive in
      // `point2` e diventa l'origine della tratta.
      if (form.service === 'restaurant' && leg.customOrigin) {
        return [
          { key: 'b', label: 'Punto di partenza', model: leg.point2 },
          { key: 'a', label: 'Indirizzo del cliente', model: leg.point },
        ];
      }
      return [{ key: 'a', label: 'Indirizzo del cliente', model: leg.point }];
    };

    // Frase che descrive il capo "sede" della tratta, direzione compresa.
    const otherEndHint = (leg) => {
      const addr = hqAddress(headquarter.value);
      const where = addr || 'la sede scelta';
      return form.service === 'escort' && leg.key === 'gone'
        ? `si arriva a ${where}`
        : `si parte da ${where}`;
    };

    const legGeocoded = (leg) => {
      if (!leg.point.latitude) return false;
      if (form.service === 'escort' && leg.customOrigin && !leg.point2.latitude) {
        return false;
      }
      // partenza personalizzata: senza le sue coordinate il planner non
      // saprebbe da dove far partire il driver
      if (form.service === 'restaurant' && leg.customOrigin && !leg.point2.latitude) {
        return false;
      }
      return true;
    };

    const freeSlots = (leg) => leg.slots.filter((s) => !s.disabled).length;

    // Orario di arrivo = orario scelto + durata del tragitto (`minute`, che
    // arriva dal calcolo km/prezzo). Serve a capire quando la corsa finisce,
    // non solo quando parte.
    const arrivalOf = (leg) => {
      if (!leg.slot || !leg.minute) return '';
      const [h, m] = leg.slot.split(':').map(Number);
      const end = new Date(2000, 0, 1, h, m + Math.round(Number(leg.minute)));
      return `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    };

    const resetLegCalc = (leg) => {
      leg.date = '';
      leg.slot = '';
      leg.slots = ALL_SLOTS.map((v) => ({ value: v, disabled: true }));
      leg.slotsError = '';
      leg.km = null;
      leg.kmRange = null;
      leg.imponibile = null;
      leg.lordoTesto = null;
      leg.imponibileAuto = null;
      leg.lordoAuto = null;
      leg.cost = null;
      leg.minute = null;
    };

    const pointToString = (p) =>
      `${p.address} ${p.street_number} ${p.cap}, ${p.city} ${p.province}`;

    const geocode = async (leg, point) => {
      if (!point.address || !point.city) {
        point.geoError = 'Servono almeno via e città.';
        return;
      }
      point.geocoding = true;
      point.geoError = '';
      try {
        const r = await prenotazioneAPI.getPoints({ text: pointToString(point) });
        const hit = (r.data || [])[0];
        if (!hit?.point?.latitude) {
          point.label = '';
          point.geoError = 'Indirizzo non trovato: controlla via, CAP e città.';
          return;
        }
        point.label = hit.label;
        point.latitude = hit.point.latitude;
        point.longitude = hit.point.longitude;
        if (legGeocoded(leg)) await calcPrice(leg);
      } catch (e) {
        point.geoError = 'Geocodifica non riuscita.';
        console.error('Geocodifica fallita:', e);
      } finally {
        point.geocoding = false;
      }
    };

    // Origine e destinazione NON seguono la direzione della tratta: per il
    // pickup l'origine è sempre la sede e la destinazione l'indirizzo del
    // cliente, come si aspettano il calcolo prezzi e il planner.
    const legPoints = (leg) => {
      if (form.service === 'escort') {
        // L'altro capo: la sede, o il punto indicato a mano.
        const other = leg.customOrigin
          ? rawPoint(leg.point2)
          : rawPoint(headquarter.value);
        const home = rawPoint(leg.point);
        return leg.key === 'gone'
          ? { origin: home, destination: other }
          : { origin: other, destination: home };
      }
      // Notturno: si parte dalla sede, a meno che non sia stato indicato un
      // punto diverso. Km, prezzo e slot devono partire tutti da lì.
      const origin = form.service === 'restaurant' && leg.customOrigin
        ? rawPoint(leg.point2)
        : rawPoint(headquarter.value);
      return { origin, destination: rawPoint(leg.point) };
    };

    const rawPoint = (p) => ({
      address: p?.address || '',
      street_number: String(p?.street_number ?? ''),
      cap: String(p?.cap ?? ''),
      city: p?.city || '',
      province: p?.province || '',
      latitude: p?.latitude || 0,
      longitude: p?.longitude || 0,
    });

    // Sul notturno il prezzo dipende dall'ora: finché non è scelta non si
    // mostra un importo, perché sarebbe quello della fascia sbagliata.
    const needsSlotForPrice = (leg) =>
      form.service === 'restaurant' && (!leg.date || !leg.slot);

    const calcPrice = async (leg) => {
      if (needsSlotForPrice(leg)) {
        leg.imponibileAuto = null;
        leg.lordoAuto = null;
        leg.imponibile = null;
        leg.lordoTesto = null;
        return;
      }
      const { origin, destination } = legPoints(leg);
      const req = {
        headquarter_id: form.headquarterId,
        origins_point: origin,
        destination_point: destination,
      };
      try {
        let r;
        if (form.service === 'restaurant') {
          // ⚠️ L'ORA DECIDE IL LISTINO: il backend usa `restaurant_night` per
          // le corse fra le 22 e le 6, `restaurant` per le altre.
          // Senza orario scelto non si quota: il numero sarebbe una
          // coincidenza (vedi `needsSlotForPrice`).
          r = await bookingAPI.getRangeRestaurant({
            ...req,
            date: new Date(`${leg.date}T${leg.slot}:00`),
          });
        } else if (form.service === 'escort') {
          r = await bookingAPI.getRangeEscort({ ...req, loaner: form.loaner });
        } else {
          r = await bookingAPI.getRangePickup({ ...req, loaner: form.loaner });
        }
        leg.km = r.data.km;
        leg.kmRange = r.data.kmRange;
        leg.cost = r.data.cost;
        leg.minute = r.data.minute;
        // ⚠️ I DUE LISTINI HANNO ORIENTAMENTO OPPOSTO, e va rispettato:
        //
        //  - NOTTURNO (`/range-restaurant`): la tariffa di fascia è il prezzo
        //    LORDO (45 €). Il backend restituisce `tot_fattura` e ne ricava
        //    l'imponibile dividendo per 1.22.
        //  - PICKUP / TRANSFER (`/range`): la tariffa È GIÀ un imponibile, e
        //    `tot_fattura` non viene proprio restituito.
        //
        // Quando il lordo è il dato di listino si conserva così com'è e
        // l'imponibile resta derivato: ricalcolare il lordo dall'imponibile
        // arrotondato darebbe 45,01 € su una tariffa da 45.
        leg.lordoAuto = r.data.tot_fattura != null ? round2(r.data.tot_fattura) : null;

        // Il motore può restituire decimali lunghi: il prezzo è denaro, si
        // ferma al centesimo. Il ricalcolo non sovrascrive un prezzo deciso
        // a mano.
        const auto = round2(r.data.imponibile ?? 0);
        const wasEdited = leg.imponibileAuto !== null && priceEdited(leg);
        leg.imponibileAuto = auto;
        if (!wasEdited) {
          leg.imponibile = auto.toFixed(2);
          leg.lordoTesto = null;
        }
      } catch (e) {
        console.error('Calcolo prezzo fallito:', e);
      }
    };

    // Cambiare la partenza cambia percorso, km e prezzo: quanto calcolato
    // finora non vale più, e va rifatto quando il nuovo punto è geocodificato.
    const onOriginModeChange = (leg) => {
      leg.slot = '';
      leg.km = null;
      leg.kmRange = null;
      leg.minute = null;
      leg.imponibile = null;
      leg.lordoTesto = null;
      leg.imponibileAuto = null;
      leg.lordoAuto = null;
      leg.slots = ALL_SLOTS.map((v) => ({ value: v, disabled: true }));
      if (!leg.customOrigin) leg.point2 = emptyPoint();
    };

    // Scelto l'orario, sul notturno si ricalcola: è l'ora a dire quale
    // listino applicare.
    const onLegSlot = async (leg) => {
      if (form.service === 'restaurant' && leg.slot) await calcPrice(leg);
    };

    // Solo Simba usa questo gestionale: può superare la capacità driver della
    // provincia. Cambiandola gli orari si ricalcolano (e l'orario scelto decade).
    const ignoreCapacity = ref(false);
    const reloadSlots = () => {
      visibleLegs.value.forEach((leg) => {
        if (leg.date && legGeocoded(leg)) onLegDate(leg);
      });
    };

    const onLegDate = async (leg) => {
      leg.slot = '';
      // cambiando data l'orario decade, e con esso il prezzo del notturno
      if (form.service === 'restaurant') {
        leg.imponibileAuto = null;
        leg.lordoAuto = null;
        leg.imponibile = null;
        leg.lordoTesto = null;
      }
      if (!leg.date || !legGeocoded(leg)) return;

      // Senza provincia il planner non sa dove cercare i driver: meglio non
      // chiamarlo affatto e dire cosa manca.
      if (!headquarter.value?.provinceId) {
        leg.slotsError = 'la sede non ha una provincia: assegnala nella scheda del partner';
        leg.slots = ALL_SLOTS.map((v) => ({ value: v, disabled: true }));
        return;
      }

      leg.slotsLoading = true;
      leg.slotsError = '';
      try {
        const { origin, destination } = legPoints(leg);
        const r = await prenotazioneAPI.calcSlot({
          originPoint: origin,
          destinationPoint: destination,
          partnerDay: leg.date,
          type: slotType(form.service, form.loaner, leg.key),
          headquarterId: headquarter.value?.id,
          ignore_capacity: ignoreCapacity.value,
        });
        // Una risposta che non è un array è un errore travestito da successo:
        // trattarla come "zero slot" manderebbe a cercare il problema fra i
        // driver invece che nella chiamata.
        if (!Array.isArray(r.data)) {
          leg.slotsError = 'risposta inattesa dal server';
          leg.slots = ALL_SLOTS.map((v) => ({ value: v, disabled: true }));
          return;
        }
        leg.slots = ALL_SLOTS.map((v) => ({ value: v, disabled: !r.data.includes(v) }));
      } catch (e) {
        // Il messaggio del server vale più del codice: 503 = mappe giù (lo
        // dice per esteso), 500 = eccezione non gestita (arriva generico).
        const status = e?.response?.status;
        const detail = e?.response?.data?.message;
        leg.slotsError = status
          ? `errore ${status}${detail ? ` — ${Array.isArray(detail) ? detail.join(' · ') : detail}` : ''}`
          : 'errore di rete';
        console.error('Slot non calcolati:', e?.response?.data ?? e);
        leg.slots = ALL_SLOTS.map((v) => ({ value: v, disabled: true }));
      } finally {
        leg.slotsLoading = false;
      }
    };

    // ---- riepilogo --------------------------------------------------------
    // Il campo è un input di testo: si normalizza la virgola decimale.
    const num = (v) => {
      const n = Number(String(v ?? '').replace(',', '.'));
      return Number.isFinite(n) ? n : 0;
    };

    const round2 = (v) => Math.round(num(v) * 100) / 100;

    // Quello che l'operatore scrive a mano segue la stessa regola: alla
    // conferma il valore diventa comunque `num()`, quindi tanto vale
    // mostrarlo già com'è.
    const normalizePrice = (leg) => {
      if (leg.imponibile === null || leg.imponibile === '') return;
      leg.imponibile = round2(leg.imponibile).toFixed(2);
    };

    const priceEdited = (leg) =>
      leg.imponibileAuto !== null && num(leg.imponibile) !== Number(leg.imponibileAuto);

    const resetPrice = (leg) => {
      leg.imponibile = leg.imponibileAuto === null ? null : leg.imponibileAuto.toFixed(2);
      leg.lordoTesto = null;
    };

    // ---- TRANSFER: prezzo IVA inclusa -------------------------------------
    // Il campo mostra il totale scritto, o quello che viene dall'imponibile;
    // scrivendolo, l'imponibile diventa totale ÷ 1,22 (al centesimo).
    const lordoCalcolato = (leg) =>
      (leg.lordoAuto != null ? Number(leg.lordoAuto) : round2(num(leg.imponibileAuto) * 1.22));
    const lordoInput = (leg) => {
      if (leg.lordoTesto !== null && leg.lordoTesto !== undefined) return leg.lordoTesto;
      if (leg.imponibile === null || leg.imponibile === '') return '';
      return round2(num(leg.imponibile) * 1.22).toFixed(2);
    };
    const setLordo = (leg, v) => {
      leg.lordoTesto = v;
      const t = String(v ?? '').trim();
      leg.imponibile = t ? round2(num(t) / 1.22).toFixed(2) : null;
    };
    const normalizeLordo = (leg) => {
      const t = String(leg.lordoTesto ?? '').trim();
      if (!t) return;
      leg.lordoTesto = round2(num(t)).toFixed(2);
    };

    // Il lordo della tratta: il prezzo di listino quando c'è ed è ancora
    // quello proposto, altrimenti ricavato dall'imponibile scritto a mano.
    // Se l'operatore corregge il prezzo è la sua correzione a comandare.
    const lordoOf = (leg) => {
      // Transfer col totale scritto a mano: vale quello, al centesimo.
      if (form.service === 'escort' && leg.lordoTesto !== null && String(leg.lordoTesto).trim() !== '') {
        return round2(num(leg.lordoTesto));
      }
      return leg.lordoAuto != null && !priceEdited(leg)
        ? Number(leg.lordoAuto)
        : num(leg.imponibile) * 1.22;
    };

    const totals = computed(() => {
      const imponibile = visibleLegs.value
        .reduce((s, l) => s + num(l.imponibile), 0);
      // NON `imponibile * 1.22`: sul notturno reintrodurrebbe il centesimo
      // perso arrotondando l'imponibile (45 → 45,01). Vedi `calcPrice`.
      const totFattura = visibleLegs.value.reduce((s, l) => s + lordoOf(l), 0);
      return { imponibile, totFattura };
    });

    // Quanto proporre come pagamento in app, e con che conti.
    //
    // Sul TRANSFER la richiesta del cliente è: imponibile + costo del
    // noleggio. Il noleggio è un costo verso il fornitore, ma su questo
    // servizio viene ribaltato sul cliente, quindi entra nella cifra da
    // saldare in app. Sugli altri servizi si parte dal totale fattura.
    const payableDefault = computed(() => {
      if (form.service === 'escort') {
        const rental = form.rental_enabled ? num(form.rental_cost) : 0;
        const amount = totals.value.imponibile + rental;
        return {
          amount,
          description: 'Transfer',
          detail: rental
            ? `Imponibile ${euro(totals.value.imponibile)} + noleggio ${euro(rental)}. `
              + 'Il noleggio resta comunque registrato come costo in COD8.'
            : `Imponibile ${euro(totals.value.imponibile)}, nessun noleggio da ribaltare.`,
        };
      }
      return {
        amount: totals.value.totFattura,
        description: form.service === 'restaurant' ? 'Prenotazione' : 'Prenotazione',
        detail: `Totale fattura: imponibile ${euro(totals.value.imponibile)} + IVA 22%.`,
      };
    });

    const selectedCustomer = computed(() =>
      customers.value.find((c) => c.id === form.customerId) || null);
    const selectedCustomerLabel = computed(() =>
      selectedCustomer.value ? customerLabel(selectedCustomer.value) : '—');

    const legsReady = computed(() =>
      visibleLegs.value.length > 0
      && visibleLegs.value.every((l) => legGeocoded(l) && l.date && l.slot));

    // Cosa manca, tratta per tratta: l'elenco finisce sotto la voce della
    // checklist, in rosso. Senza, un Transfer a cui manca solo l'orario del
    // ritorno sembrava pronto e il pulsante restava spento senza motivo.
    const elenco = (voci) =>
      (voci.length < 2 ? voci.join('') : `${voci.slice(0, -1).join(', ')} e ${voci[voci.length - 1]}`);
    const legsMancanti = computed(() => {
      if (!visibleLegs.value.length) return ['Aggiungi almeno una tratta.'];
      return visibleLegs.value.flatMap((l) => {
        const manca = [];
        if (!legGeocoded(l)) manca.push("l'indirizzo");
        if (!l.date) manca.push('la data');
        if (!l.slot) manca.push("l'orario");
        return manca.length ? [`${l.title}: manca ${elenco(manca)}`] : [];
      });
    });

    // Capienza: avviso, non blocco. Il numero di posti non sta a schema e la
    // capienza vera del mezzo noleggiato la conosce chi prenota — ma 12
    // passeggeri su un pulmino da 9 è quasi sempre uno sbaglio di battitura.
    const seatWarning = computed(() => {
      const v = vehicleOf(form.vehicle_type);
      const n = Number(form.passengers);
      if (!v || !n || n <= v.seats) return '';
      return `${n} passeggeri su «${v.label}» (${v.seats} posti): verifica il mezzo.`;
    });

    const isEscortService = computed(() => form.service === 'escort');

    // Il noleggio si propone su OGNI Transfer con un mezzo scelto: `rentable`
    // decide solo se la spunta parte attiva, perché chi prenota sa se quel
    // mezzo è stato noleggiato.
    const showRental = computed(() =>
      isEscortService.value && !!form.vehicle_type);

    // Mezzo che di norma NON si noleggia: la spunta parte spenta e si dice
    // perché, invece di far sparire la sezione.
    const rentalUnusual = computed(() => !vehicleOf(form.vehicle_type)?.rentable);

    const suppliers = ref([]);
    const suppliersLoading = ref(false);

    const loadSuppliers = async () => {
      if (suppliers.value.length || suppliersLoading.value) return;
      suppliersLoading.value = true;
      try {
        suppliers.value = await supplierAPI.getSuppliers({ onlyActive: true });
      } catch (e) {
        // 403 = risorsa riservata a Simba: la sezione resta vuota con il suo
        // avviso, non si blocca la creazione della prenotazione.
        console.warn('Fornitori non disponibili:', e?.message);
        suppliers.value = [];
      } finally {
        suppliersLoading.value = false;
      }
    };

    const goSuppliers = () => router.push('/supplier');

    // I fornitori si caricano solo quando servono davvero: sono una risorsa
    // riservata a Simba, e chiederli su ogni prenotazione produrrebbe un 403
    // a vuoto per gli utenti partner.
    watch(showRental, (on) => {
      if (on) loadSuppliers();
      else {
        form.rental_enabled = false;
        form.supplierId = '';
        form.rental_cost = null;
      }
    });

    const checklist = computed(() => [
      { label: 'Partner e sede', ok: !!form.headquarterId },
      { label: 'Servizio', ok: !!form.service },
      { label: 'Cliente', ok: !!form.customerId },
      ...(needsCar.value ? [{ label: 'Auto', ok: !!form.carId }] : []),
      // Solo il mezzo: i passeggeri sono facoltativi (la colonna è nullable e
      // POST /booking non li pretende — a differenza delle richieste dall'app).
      ...(isEscortService.value
        ? [{ label: 'Mezzo', ok: !!form.vehicle_type }]
        : []),
      // Solo se il noleggio è stato attivato: registrarlo è facoltativo,
      // ma a metà no — senza fornitore il costo non è attribuibile.
      ...(showRental.value && form.rental_enabled
        ? [{ label: 'Fornitore del mezzo', ok: !!form.supplierId }]
        : []),
      {
        label: 'Tratte con indirizzo, data e orario',
        ok: legsReady.value,
        details: legsMancanti.value,
      },
    ]);

    const canSubmit = computed(() => checklist.value.every((c) => c.ok));

    // ---- creazione --------------------------------------------------------
    // Per ogni tratta il planner calcola gli orari, verifica che in quell'ora
    // esista copertura e CREA i planner — ma NON assegna nessuno: driver e
    // jolly si scelgono a mano dalla pagina di dettaglio (`assign: false`).
    //
    // Un orario senza driver liberi in provincia non è prenotabile, e nemmeno
    // uno oltre la capacità driver della provincia (salvo `ignoreCapacity`).
    //
    // ⚠️ Una tratta scoperta non occupa nessun DRIVER (la ricerca guarda i
    // planner legati a un driver), ma occupa la CAPACITÀ della provincia.
    const buildLeg = async (leg) => {
      const { origin, destination } = legPoints(leg);
      // istante vero, non orologio da parete (vedi services/datetime.js)
      const when = toInstant(leg.date, leg.slot);

      const res = await prenotazioneAPI.calcDuration({
        originPoint: origin,
        destinationPoint: destination,
        partnerTime: when,
        type: slotType(form.service, form.loaner, leg.key),
        headquarterId: headquarter.value?.id,
        assign: false,
        ignore_capacity: ignoreCapacity.value,
      });

      // best-driver risponde 200 anche quando fallisce: il controllo è sul corpo.
      if (res?.data?.error || !res?.data?.driver) {
        throw new Error(
          res?.data?.error === 'No drivers available for this hour'
            ? `Nessun driver libero per ${leg.title.toLowerCase()} alle ${leg.slot}: scegli un altro orario.`
            : res?.data?.error || 'Il planner non ha restituito un driver.',
        );
      }

      const { driver, plannerDriver, plannerJolly } = res.data;
      const planners = [plannerDriver];
      // Il jolly torna null quando il servizio non lo prevede (auto di cortesia).
      if (plannerJolly) planners.push(plannerJolly);

      // ORARI DELLA TRATTA — `date_customer` è l'orario dal lato del
      // CLIENTE (casa), `date_partner` quello dal lato della SEDE. La lettura
      // (lista booking e GET /booking/schedule) ne ricava partenza e arrivo
      // dalla direzione: all'andata si parte da casa, al ritorno dalla sede.
      //
      // Sul PICKUP l'orario scelto è l'arrivo in sede, quindi va in
      // `date_partner` e la partenza dal cliente la calcola il planner.
      //
      // Sul TRANSFER l'orario scelto è la PARTENZA (in `escortGone` il
      // `realStartTime` coincide con l'ora richiesta) e il planner non produce
      // il viaggio `date_c1_p1`: l'arrivo si ricava dalla durata. All'andata
      // (casa → sede) la partenza è l'orario del cliente; al ritorno
      // (sede → casa) è quello della sede, e l'arrivo a casa va nel cliente.
      const isTransferLeg = form.service === 'escort';
      const arrivalWhen = toInstant(leg.date, arrivalOf(leg)) ?? when;
      const transferReturn = isTransferLeg && leg.key === 'return';

      const common = {
        date_partner: isTransferLeg ? (transferReturn ? when : arrivalWhen) : when,
        date_customer: isTransferLeg
          ? (transferReturn ? arrivalWhen : when)
          : tripDate(driver.trips, 'date_c1_p1'),
        // Orario di partenza del DRIVER. Dipende da dove si trova, e il
        // driver non è ancora scelto: quello del candidato proposto dal
        // motore sarebbe l'orario di un'altra persona.
        //
        // Non si può però lasciarlo VUOTO: l'app cliente non deserializza una
        // tappa con date a null (è il motivo per cui la conversione delle
        // richieste valorizza tutte e tre le date). Si usa quindi l'orario
        // scelto come segnaposto, e chi assegna il driver lo corregge.
        date_driver: when,
        date_jolly_scan: '',
        true_date_partner: '',
        true_date_customer: '',
        true_date_driver: '',
        simba_km: leg.km,
        simba_minute: leg.minute,
        // il notturno è sempre una tratta di ritorno
        type_booking: form.service === 'restaurant' ? 'return' : leg.key,
        order: 0,
        planner: null,
        planners,
      };

      if (form.service === 'escort') {
        return {
          ...common,
          // ⚠️ La tratta escort NON ha una colonna `simba_cost` — quella che
          // il gestionale partner le assegna finisce nel vuoto. Le colonne
          // vere sono le stesse del pickup: imponibile e fascia km.
          simba_km_range: leg.kmRange,
          simba_imponibile: num(leg.imponibile),
          // il flag dice che la corsa ha lavorazioni collegate
          tasks: form.tasks.length > 0,
          driver_escort: null,
          // stessi capi usati per km, slot e prezzo: una regola sola
          point_origin_escort: legPoints(leg).origin,
          point_destination_escort: legPoints(leg).destination,
        };
      }
      return {
        ...common,
        simba_km_range: leg.kmRange,
        // il prezzo che finisce sulla tratta è quello a schermo, non quello
        // proposto dal motore: l'operatore può averlo corretto
        simba_imponibile: num(leg.imponibile),
        qrCode: '',
        driver_pickup: null,
        point: rawPoint(leg.point),
      };
    };

    const submit = async () => {
      submitting.value = true;
      try {
        const chosen = visibleLegs.value;
        const built = [];
        for (const leg of chosen) {
          // in sequenza di proposito: il planner assegna i driver uno alla
          // volta, e la seconda tratta deve vedere l'occupazione della prima
          // eslint-disable-next-line no-await-in-loop
          built.push(await buildLeg(leg));
        }

        const isEscort = form.service === 'escort';
        const type = form.service === 'restaurant'
          ? 'return'
          : chosen.length === 2 ? 'both' : chosen[0].key;

        // `pay: true` = il cliente la vede come da SALDARE in app; senza
        // spunta gli arriva solo da accettare.
        const tasks = form.tasks
          .filter((t) => t.description?.trim() || num(t.cost))
          .map((t) => ({
            description: t.description?.trim() || 'Prenotazione',
            cost: num(t.cost),
            pay: !!t.pay,
            accepted: false,
            // Il tenant proprietario della lavorazione. Va mandato da qui:
            // i task nascono annidati nella POST del booking e nessuno lato
            // server li firma, quindi restavano con `partnerId` NULL — e una
            // riga senza tenant è invisibile al COD5 per partner, che filtra
            // proprio su quella colonna.
            //
            // Per un utente PARTNER questo valore viene comunque sovrascritto
            // dal server (`TenantCrudAuth.persist` impone il proprio tenant):
            // conta per il superadmin, che è chi crea da questa pagina.
            partnerId: form.partnerId,
          }));

        const booking = {
          payed: false,
          // le colonne sono decimal(10,2): si manda quello che è a schermo,
          // non un totale con dodici decimali che il database troncherebbe
          imponibile: round2(totals.value.imponibile),
          tot_fattura: round2(totals.value.totFattura),
          rif_fattura: 0,
          liquidato: false,
          // L'ORARIO SCELTO per la prima tratta, preso dal form e non dalle
          // colonne della tratta: su pickup e transfer `date_partner` e
          // `date_customer` significano cose opposte (arrivo/partenza), quindi
          // nessuna delle due è "l'orario del booking" per entrambi.
          data: toInstant(chosen[0].date, chosen[0].slot),
          over_twentysix: form.over_twentysix,
          loaner: form.loaner,
          targa_loaner: form.targa_loaner || '',
          state: 'before',
          type,
          note: form.note || '',
          customer: { id: form.customerId },
          headquarter: { id: form.headquarterId },
          tasks,
          // 'pickup_loaner' esiste solo sul pickup: il transfer con auto del
          // partner resta 'escort'.
          type_prenotazione: isEscort
            ? 'escort'
            : form.loaner ? 'pickup_loaner' : 'pickup',
          ...(needsCar.value ? { car: { id: form.carId } } : {}),
          // Snapshot Transfer: stesse colonne che la conversione di una
          // richiesta dall'app scrive sul booking.
          ...(isEscort
            ? {
              vehicle_type: form.vehicle_type || null,
              passengers: Number(form.passengers) || null,
            }
            : {}),
          ...(isEscort
            ? { escort_bookings: built }
            : { pickup_bookings: built }),
        };

        const res = await bookingAPI.addBooking(booking);

        // NOLEGGIO: record separato, e solo ora si può creare — ha bisogno
        // dell'id del booking. Se fallisce, la prenotazione resta valida: si
        // dice cosa manca invece di far credere che sia andato storto tutto.
        let rentalFailed = false;
        // Mezzo che di norma si noleggia, ma nessun noleggio registrato: non è
        // un errore (l'auto poteva essere nostra), ma va DETTO. Senza, la
        // prenotazione risulta creata e il costo del mezzo non compare mai nel
        // COD8, senza che nulla lo abbia segnalato.
        const rentalMissing =
          showRental.value
          && !form.rental_enabled
          && !!vehicleOf(form.vehicle_type)?.rentable;

        if (showRental.value && form.rental_enabled && form.supplierId) {
          try {
            await supplierAPI.createRental({
              bookingId: res.data.id,
              supplierId: form.supplierId,
              vehicleType: form.vehicle_type,
              rentalDate: chosen[0].date,
              cost: form.rental_cost,
            });
          } catch (e) {
            rentalFailed = true;
            console.error('Noleggio non registrato:', e);
          }
        }

        // Best-effort: il booking a questo punto esiste. Un errore qui non
        // deve far credere che la creazione sia fallita. Una corsa già
        // avvenuta non si annuncia: il cliente l'ha già fatta.
        const giaAvvenuta = chosen.every((l) => l.date < giornoDiOggi());
        if (!giaAvvenuta) {
          try {
            await bookingAPI.addBookingNotification({
              customer: selectedCustomer.value,
              booking: res.data,
            });
          } catch (e) {
            console.warn('Notifica nuova prenotazione fallita:', e?.message);
          }
        }

        if (rentalFailed) {
          ElMessage.warning(
            'Prenotazione creata, ma il noleggio non è stato registrato: '
            + 'aggiungilo dal dettaglio, altrimenti il costo non entra nel COD8.',
          );
        } else if (rentalMissing) {
          ElMessage.warning({
            message:
              'Prenotazione creata senza noleggio: il costo del mezzo NON '
              + 'comparirà nel COD8. Se il mezzo è di un fornitore, registralo '
              + 'dal dettaglio della prenotazione.',
            duration: 8000,
          });
        } else ElMessage.success('Prenotazione creata.');
        router.push(`/booking/detail/${res.data.id}`);
      } catch (e) {
        // Gli errori HTTP li annuncia già l'interceptor di API.js; qui restano
        // quelli del planner, che arrivano come Error normali.
        if (!e?.response) ElMessage.error(e.message || 'Creazione non riuscita.');
        console.error('Creazione prenotazione fallita:', e);
      } finally {
        submitting.value = false;
      }
    };

    // ---- etichette --------------------------------------------------------
    const addTask = () => form.tasks.push({ description: '', cost: '', pay: false });

    const addTripTask = () => form.tasks.push({
      description: payableDefault.value.description,
      cost: payableDefault.value.amount.toFixed(2),
      pay: true,
    });
    // Indirizzo completo della sede, nello stesso formato del resto della app.
    const hqAddress = (h) => (h
      ? [h.address, h.street_number, h.cap, h.city, h.province]
        .filter(Boolean).join(' ').replace(/\s+/g, ' ').trim()
      : '');

    // L'etichetta è anche quella che resta nella casella dopo la scelta:
    // nome E indirizzo, non solo il nome.
    const hqLabel = (h) => {
      const name = h?.name || 'Sede';
      const addr = hqAddress(h);
      return addr ? `${name} — ${addr}` : name;
    };
    const carLabel = (c) =>
      [c.marca, c.modello].filter(Boolean).join(' ') + (c.targa ? ` · ${c.targa}` : '');
    function customerLabel(c) {
      const n = [c.name, c.surname].filter(Boolean).join(' ');
      return c.company_name ? `${c.company_name} (${n})` : n || c.mail;
    }
    const euro = (n) => `€ ${num(n).toFixed(2).replace('.', ',')}`;
    const dLabel = (v) => {
      if (!v) return '—';
      const d = new Date(v);
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
    };
    const goBack = () => router.push('/booking');

    return {
      form, legs, activeLegs, visibleLegs, partners, partner, partnerLoading,
      headquarters, headquarter, capabilities, serviceLabel, needsCar,
      needsJolly, provinceName, tariffServiceLabel,
      customers, customersLoading, cars, carsLoading, submitting,
      totals, payableDefault, checklist, canSubmit, selectedCustomerLabel,
      VEHICLES, seatWarning,
      showRental, rentalUnusual, suppliers, suppliersLoading, goSuppliers,
      onPartnerChange, onHeadquarterChange, setService, searchCustomers,
      onCustomerChange, pointsOf, legGeocoded, freeSlots, geocode, onLegDate,
      ignoreCapacity, reloadSlots,
      onLegSlot, arrivalOf, isNightHour, needsSlotForPrice, onOriginModeChange,
      addTask, addTripTask, priceEdited, resetPrice, normalizePrice,
      lordoInput, setLordo, normalizeLordo, lordoCalcolato,
      submit, goBack, otherEndHint,
      hqLabel, hqAddress, carLabel, customerLabel, euro, dLabel,
    };
  },
};
</script>

<style scoped>
.bkn { padding: 4px 0 32px; }

/* scelta della partenza sul notturno */
.bkn__originSwitch {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin-bottom: 10px;
}
.bkn__originHint { font-size: 12px; color: var(--text-tertiary); }

/* arrivo stimato dopo la scelta dell'orario */
.bkn__arrival {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin-top: 10px; padding: 9px 12px; border-radius: 9px;
  background: var(--bg-secondary); border: 1px solid var(--border-color);
  font-size: 13px; color: var(--text-secondary);
}
.bkn__arrivalMain b {
  color: var(--text-primary); font-variant-numeric: tabular-nums;
}
.bkn__arrivalSub { font-size: 12px; color: var(--text-tertiary); }
.bkn__fascia {
  margin-left: auto; font-size: 11px; font-weight: 700; border-radius: 20px;
  padding: 2px 9px; color: var(--svc-pickup-ink); background: var(--svc-pickup-bg);
}
.bkn__fascia--night {
  color: var(--svc-notturno); background: var(--svc-notturno-bg);
}
.bkn__spacer { flex: 1; }

.bkn__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-bottom: 18px;
}
.bkn__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.bkn__back:hover { border-color: var(--brand-violet); }
.bkn__title { margin: 0; font-size: 21px; color: var(--text-primary); }
.bkn__sub { margin: 4px 0 0; font-size: 13px; color: var(--text-tertiary); }

.bkn__grid {
  display: grid; grid-template-columns: minmax(0, 1fr) 330px;
  gap: 16px; align-items: start;
}
@media (max-width: 1050px) { .bkn__grid { grid-template-columns: 1fr; } }
.bkn__col { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.bkn__aside {
  position: sticky; top: 16px; display: flex; flex-direction: column; gap: 16px;
}
@media (max-width: 1050px) { .bkn__aside { position: static; } }

.bkn__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.bkn__card.is-off { opacity: 0.55; }
.bkn__cardHead {
  display: flex; align-items: center; gap: 10px; padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
}
.bkn__cardHead h3 { margin: 0; font-size: 14px; color: var(--text-primary); }
.bkn__step {
  width: 22px; height: 22px; flex: none; border-radius: 50%;
  display: grid; place-items: center; font-size: 11px; font-weight: 700;
  color: #fff; background: var(--brand-violet);
}
.bkn__cardBody { padding: 16px; }

.bkn__row { display: flex; gap: 14px; flex-wrap: wrap; }
.bkn__row--tight { margin-top: 12px; }
.bkn__field { flex: 1; min-width: 210px; }
.bkn__field label {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.bkn__opt { font-weight: 500; text-transform: none; letter-spacing: 0; }

/* servizi: la scelta è vincolata dalle capability del partner */
.bkn__services { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 16px; }
.bkn__svc {
  display: flex; flex-direction: column; gap: 6px; align-items: flex-start;
  padding: 11px 13px; cursor: pointer; font-family: inherit; text-align: left;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-md); min-width: 190px;
}
.bkn__svc:hover { border-color: var(--brand-violet); }
.bkn__svc.is-active {
  border-color: var(--brand-violet); box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.bkn__svcD { font-size: 11.5px; color: var(--text-tertiary); }
.bkn__opts { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; margin-top: 14px; }

.bkn__capacity { margin: 12px 0 0; }
.bkn__capacity .bkn__hint { margin-top: 4px; }
.bkn__warn {
  margin: 14px 0 0; padding: 10px 13px; border-radius: 9px;
  font-size: 12.5px; line-height: 1.5;
  color: var(--state-danger); background: var(--state-danger-bg);
  border: 1px solid var(--state-danger-line);
}
.bkn__warn--soft {
  color: var(--state-attn); background: var(--state-attn-bg);
  border-color: var(--state-attn-line);
}
.bkn__warn code, .bkn__hint code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 11.5px;
}

/* tratta */
.bkn__leg {
  border: 1px solid var(--border-color); border-radius: var(--radius-md);
  padding: 14px; background: var(--bg-secondary);
}
.bkn__leg + .bkn__leg { margin-top: 12px; }
.bkn__legHead { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.bkn__legTag {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; padding: 2px 8px; border-radius: 5px;
  color: var(--state-muted); background: var(--state-muted-bg);
  border: 1px solid var(--state-muted-line);
}
.bkn__legKm {
  font-size: 12px; color: var(--text-secondary); font-variant-numeric: tabular-nums;
}
.bkn__points { display: flex; flex-direction: column; gap: 12px; }
.bkn__pointLbl {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.bkn__addr { display: flex; gap: 8px; flex-wrap: wrap; }
.bkn__addrStreet { flex: 3; min-width: 160px; }
.bkn__addrN { width: 70px; }
.bkn__addrCap { width: 90px; }
.bkn__addrCity { flex: 2; min-width: 120px; }
.bkn__addrPr { width: 70px; }

.bkn__hint { margin: 6px 0 0; font-size: 11.5px; line-height: 1.45; color: var(--text-tertiary); }
.bkn__hint--attn { color: var(--state-attn); }
.bkn__hint--ok { color: var(--state-ok); }

/* Colonne fisse per importo, spunta e azione: a flex la descrizione si
   prendeva tutto e l'importo restava tagliato. */
.bkn__taskHead,
.bkn__task {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px 84px 72px;
  gap: 10px; align-items: center;
}
.bkn__task { margin-bottom: 10px; }
.bkn__taskHead {
  margin-bottom: 6px;
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.bkn__taskNum { text-align: right; }
@media (max-width: 720px) {
  .bkn__taskHead { display: none; }
  .bkn__task { grid-template-columns: 1fr 1fr; }
}
.bkn__taskCost :deep(.el-input__inner) { text-align: right; }
.bkn__taskPay { margin: 0; }
.bkn__taskDel { justify-self: start; }

/* prezzo della tratta: proposta a sinistra, stato del calcolo a destra */
.bkn__price {
  display: flex; gap: 14px; align-items: flex-start; flex-wrap: wrap;
  margin-top: 14px; padding-top: 12px; border-top: 1px dashed var(--border-color);
}
.bkn__field--price { flex: none; width: 200px; min-width: 200px; }
/* mezzo e passeggeri del Transfer: larghezza propria, non a tutta riga */
.bkn__field--veh { flex: none; min-width: 0; }

/* noleggio del mezzo da fornitore */
.bkn__rental {
  margin-top: 14px; padding: 12px 14px; border-radius: var(--radius-md);
  background: var(--bg-secondary); border: 1px solid var(--border-color);
}
.bkn__rentalHead { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.bkn__rentalTitle {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.bkn__rental .bkn__row { margin-top: 12px; }
.bkn__priceSide { flex: 1; min-width: 200px; padding-top: 22px; }
.bkn__hint--edit { color: var(--state-active); }

/* opzione sede su due righe: nome sopra, indirizzo sotto */
.bkn__hqOpt { height: auto !important; line-height: 1.35 !important; padding: 7px 20px !important; }
.bkn__hqName { display: block; font-weight: 600; }
.bkn__hqAddr { display: block; font-size: 11.5px; color: var(--text-tertiary); }
.bkn__link {
  border: 0; background: none; padding: 0 0 0 4px; cursor: pointer;
  font: inherit; color: var(--brand-violet); text-decoration: underline;
}

/* riepilogo */
.bkn__kv {
  display: flex; justify-content: space-between; gap: 12px; padding: 6px 0;
  font-size: 13px; border-bottom: 1px solid var(--border-color);
}
.bkn__kv span { color: var(--text-tertiary); }
.bkn__kv b { color: var(--text-primary); font-weight: 650; text-align: right; }
.bkn__sumLeg { margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--border-color); }
.bkn__sumLegTop {
  display: flex; justify-content: space-between; gap: 10px;
  font-size: 12.5px; color: var(--text-secondary);
}
.bkn__sumLegTop b { color: var(--text-primary); }
.bkn__sumLegRow {
  display: flex; justify-content: space-between; gap: 10px; margin-top: 4px;
  font-size: 12px; color: var(--text-tertiary);
}
.bkn__sumLegRow b { font-variant-numeric: tabular-nums; color: var(--text-secondary); }
.bkn__tot { margin-top: 14px; padding-top: 12px; border-top: 2px solid var(--border-color); }
.bkn__totRow {
  display: flex; justify-content: space-between; gap: 10px; padding: 3px 0;
  font-size: 13px; color: var(--text-secondary);
}
.bkn__totRow b { font-variant-numeric: tabular-nums; color: var(--text-primary); }
.bkn__totRow--big b { font-size: 18px; font-weight: 750; }

.bkn__check {
  display: flex; align-items: flex-start; gap: 9px; padding: 5px 0;
  font-size: 13px;
  /* Quello che manca si vede: in grigio, come le voci già fatte, una
     checklist incompleta sembrava solo un elenco di titoli. */
  color: var(--state-danger); font-weight: 600;
}
.bkn__checkIco {
  width: 18px; height: 18px; flex: none; border-radius: 50%;
  display: grid; place-items: center; font-size: 11px; font-weight: 700;
  color: var(--state-danger); background: var(--state-danger-bg);
  border: 1px solid var(--state-danger-line);
}
.bkn__checkWhy { display: block; font-weight: 500; font-size: 12px; margin-top: 1px; }
.bkn__check.is-ok { color: var(--text-primary); font-weight: 400; }
.bkn__check.is-ok .bkn__checkIco {
  color: #fff; background: var(--state-ok); border-color: var(--state-ok);
}
.bkn__submit { width: 100%; margin-top: 14px; }
</style>
